import { useEffect, useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { shallow } from 'zustand/shallow';
import { toast } from 'react-toastify';

import Loader from '@shared/components/Loader';
import OperatorOverview from '@shared/components/OperatorOverview';
import { toOperatorOverviewProps, toDocuments, toFleetPanels } from '@shared/utils/operatorOverviewProps';
import { defaultErrorMessage, defaultSuccessMessage } from '@/helpers';
import { fetchOperator, fetchFleetsByOperator, fetchFileLinks } from '@/helpers/mvMasterApi';
import { useOperatorProfileStore } from '@/store/OperatorProfileStore';
import AircraftProfile from '@/pages/AircraftProfile';

// Read-only: operators are still edited in Maverick, so there is no Edit action here.
const OperatorProfile = () => {
  const { operatorId, setOpenOperatorProfileView } = useOperatorProfileStore(
    (state) => ({
      operatorId: state.operatorId,
      setOpenOperatorProfileView: state.setOpenOperatorProfileView,
    }),
    shallow
  );

  const [ loading, setLoading ] = useState(false);
  const [ operator, setOperator ] = useState(null);
  const [ fleets, setFleets ] = useState([]);
  const [ documents, setDocuments ] = useState([]);
  const [ profileAircraft, setProfileAircraft ] = useState(null);

  useEffect(() => {
    document.body.classList?.add('hide-scroll-on-modal-open');
    return () => {
      document.body.classList?.remove('hide-scroll-on-modal-open');
    };
  }, []);

  useEffect(() => {
    if (!operatorId) {
      setOperator(null);
      setFleets([]);
      setDocuments([]);
      return undefined;
    }
    // Results from a superseded operator are dropped rather than overwriting the current one.
    let active = true;

    (async () => {
      setLoading(true);
      let record = null;
      try {
        record = await fetchOperator(operatorId);
        if (active) {
          setOperator(record ?? null);
        }
      } catch (error) {
        if (active) {
          setOperator(null);
          toast.error('Failed to load the operator. Please try again.', defaultErrorMessage);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
      if (!active || !record) {
        return;
      }
      // The overview is already usable; these fill in behind it rather than holding the
      // loader over Close Window, and say so when they fail instead of reading as empty.
      fetchFleetsByOperator(operatorId)
        .then((fleetData) => active && setFleets(fleetData))
        .catch(() => active && toast.error('Failed to load fleets. Please try again.', defaultErrorMessage));
      fetchFileLinks(record.files)
        .then((files) => active && setDocuments(files))
        .catch(() => active && toast.error('Failed to load documents. Please try again.', defaultErrorMessage));
    })();

    return () => {
      active = false;
    };
  }, [ operatorId ]);

  const closeDialog = () => setOpenOperatorProfileView(false);

  return (
    <Dialog
      title="OPERATOR OVERVIEW"
      onClose={closeDialog}
      className="add-contact-details-model right-open-modal w-650px"
    >
      {loading && <Loader />}
      {operator && (
        <OperatorOverview
          {...toOperatorOverviewProps(operator)}
          fleets={toFleetPanels(fleets)}
          documents={toDocuments(documents)}
          onAircraftClick={(aircraft) => setProfileAircraft({ id: aircraft.id })}
          onCopyEmail={() => toast.success('copied!', defaultSuccessMessage)}
        />
      )}
      <div className="model-footer-action">
        <Button
          className="height-42 mob-width-full btn-large-padding border-0 fs-14 font-600"
          themeColor="primary"
          onClick={closeDialog}
        >
          Close Window
        </Button>
      </div>
      {profileAircraft && (
        <AircraftProfile
          toggleDialog={() => setProfileAircraft(null)}
          aircraftId={profileAircraft.id}
        />
      )}
    </Dialog>
  );
};

export default OperatorProfile;
